import errorLogger from 'utils/errorLogger';
import Config from 'Config';

const GIS_CLIENT_JS = 'https://accounts.google.com/gsi/client';
const PENDING_KEY = 'remind.googleAuth.pending';

/** Fixed path so join URLs do not need per-class Google redirect URIs. */
export const GOOGLE_AUTH_CALLBACK_PATH = '/log_in';

type GoogleCodeClient = {
  requestCode: () => void;
};

type GoogleOauth2 = {
  initCodeClient: (config: {
    client_id: string;
    scope: string;
    include_granted_scopes?: boolean;
    ux_mode: 'redirect' | 'popup';
    redirect_uri: string;
    state: string;
    prompt?: string;
  }) => GoogleCodeClient;
};

type PendingAuth = {
  state: string;
  mergeToken?: string | null;
  join?: string;
  locationOnAuth?: string;
  urlOnAuth?: string;
  extraQuery?: Record<string, string>;
};

export type GoogleAuthReturnContext = {
  join?: string;
  locationOnAuth?: string;
  urlOnAuth?: string;
  extraQuery?: Record<string, string>;
};

export type GoogleRedirectResult = {
  code?: string;
  error?: string;
  mergeToken?: string | null;
};

let loadId: string | null = null;
let pendingReturn: GoogleAuthReturnContext | null = null;

function getOauth2(): GoogleOauth2 | undefined {
  return (window as Window & { google?: { accounts?: { oauth2?: GoogleOauth2 } } }).google?.accounts
    ?.oauth2;
}

/**
 * URI Google will send the user back to. Must be listed as an authorized
 * redirect URI on the web OAuth client.
 */
export function getGoogleRedirectUri(): string {
  return `${window.location.origin}${GOOGLE_AUTH_CALLBACK_PATH}`;
}

function generateState(): string {
  if (window.crypto && typeof window.crypto.randomUUID === 'function') {
    return window.crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function captureReturnContext(): GoogleAuthReturnContext {
  const { pathname, search } = window.location;
  const params = new URLSearchParams(search);
  const extraQuery: Record<string, string> = {};
  params.forEach((value, key) => {
    extraQuery[key] = value;
  });

  const joinMatch = pathname.match(/^\/join\/([^/]+)$/);
  if (joinMatch && joinMatch[1] !== 'role_picker') {
    return { join: joinMatch[1], extraQuery };
  }
  if (pathname === '/sign_up') {
    return { locationOnAuth: '/landing', extraQuery };
  }

  return {
    join: extraQuery.join,
    locationOnAuth: extraQuery.locationOnAuth,
    urlOnAuth: extraQuery.urlOnAuth,
    extraQuery,
  };
}

function loadGis(): Promise<GoogleOauth2> {
  const existing = getOauth2();
  if (existing) {
    return Promise.resolve(existing);
  }

  return new Promise((resolve, reject) => {
    const $script = require('scriptjs');

    if (!loadId) {
      loadId = 'gis-client';
      $script(GIS_CLIENT_JS, loadId);
    }

    $script.ready(loadId, () => {
      const oauth2 = getOauth2();
      if (oauth2) {
        resolve(oauth2);
        return;
      }
      reject(new Error('Google Identity Services failed to load'));
    });
  });
}

/**
 * Loads GIS. Used by `startAuthorization`; not called on button mount so
 * tests that render `GoogleSignIn` do not leave a hanging `scriptjs` load.
 */
function initWithAuthInstance(callback: () => void = function () {}) {
  loadGis()
    .then(() => {
      callback();
    })
    .catch((error) => {
      errorLogger('GoogleAPI init failed')(error);
    });
}

/**
 * Starts a full-page Google OAuth redirect. The page unloads; the
 * authorization code is consumed after Google returns to `/log_in`.
 */
function startAuthorization(options: { mergeToken?: string | null } = {}): Promise<void> {
  const clientId = Config.Environment.GOOGLE_OAUTH_CLIENT_ID;
  if (!clientId) {
    return Promise.reject(new Error('Missing GOOGLE_OAUTH_CLIENT_ID'));
  }

  const pending: PendingAuth = {
    state: generateState(),
    mergeToken: options.mergeToken,
    ...captureReturnContext(),
  };

  try {
    window.sessionStorage.setItem(PENDING_KEY, JSON.stringify(pending));
  } catch (error) {
    errorLogger('GoogleAPI failed to persist pending auth')(error as Error);
    return Promise.reject(error);
  }

  return loadGis()
    .then((oauth2) => {
      const client = oauth2.initCodeClient({
        client_id: clientId,
        scope: 'openid email profile',
        include_granted_scopes: false,
        ux_mode: 'redirect',
        redirect_uri: getGoogleRedirectUri(),
        state: pending.state,
        prompt: 'select_account',
      });
      client.requestCode();
    })
    .catch((error) => {
      errorLogger('GoogleAPI startAuthorization failed')(error);
      throw error;
    });
}

function stripOauthParamsFromUrl() {
  const params = new URLSearchParams(window.location.search);
  [
    'code',
    'state',
    'error',
    'error_description',
    'scope',
    'authuser',
    'prompt',
    'hd',
    'iss',
  ].forEach((key) => {
    params.delete(key);
  });
  const remaining = params.toString();
  const nextUrl = `${window.location.pathname}${remaining ? `?${remaining}` : ''}${
    window.location.hash
  }`;
  window.history.replaceState({}, '', nextUrl);
}

/**
 * Reads `code` / `error` from the current URL after Google redirects back.
 * Returns null when this page load is not a Google OAuth return.
 */
function consumeRedirectResult(): GoogleRedirectResult | null {
  const params = new URLSearchParams(window.location.search);
  const code = params.get('code');
  const error = params.get('error');
  const state = params.get('state');

  if (!code && !error) {
    return null;
  }

  let pending: PendingAuth | null = null;
  try {
    const raw = window.sessionStorage.getItem(PENDING_KEY);
    if (raw) {
      pending = JSON.parse(raw) as PendingAuth;
    }
  } catch (err) {
    errorLogger('GoogleAPI failed to read pending auth')(err as Error);
  }
  try {
    window.sessionStorage.removeItem(PENDING_KEY);
  } catch (err) {
    errorLogger('GoogleAPI failed to clear pending auth')(err as Error);
  }

  stripOauthParamsFromUrl();

  if (!pending || pending.state !== state) {
    // Ignore forged /log_in?error= URLs; still reject a code without state.
    return error && !code ? null : { error: 'state_mismatch' };
  }

  if (error) {
    return { error };
  }

  pendingReturn = {
    join: pending.join,
    locationOnAuth: pending.locationOnAuth,
    urlOnAuth: pending.urlOnAuth,
    extraQuery: pending.extraQuery,
  };

  return { code: code || undefined, mergeToken: pending.mergeToken };
}

/**
 * Post-login destination captured before the Google redirect. Cleared on read
 * so SignInPage can restore `join` / `locationOnAuth` after AUTH_SUCCESS.
 */
function takePendingReturn(): GoogleAuthReturnContext | null {
  const value = pendingReturn;
  pendingReturn = null;
  return value;
}

/**
 * GIS has no gapi.auth2 session to clear. The old popup path also left the
 * Google account signed in in the browser; keep that behavior (no revoke).
 */
function signOut(): Promise<void> {
  return Promise.resolve();
}

export default {
  initWithAuthInstance,
  startAuthorization,
  consumeRedirectResult,
  takePendingReturn,
  signOut,
  getGoogleRedirectUri,
};
